import { useState, useEffect, useCallback, useContext } from "react";
import { useHttpClient } from "./http-hook";
import { AuthContext } from "../context/auth-context";

const useFolders = () => {
	const [folders, setFolders] = useState([]);
	const { isLoading, error, sendRequest, clearError } = useHttpClient();
	const auth = useContext(AuthContext);
	const [uploading, setUploading] = useState(false);

	const fetchFolders = useCallback(async () => {
		try {
			const response = await sendRequest("/api/folders", "GET", null, {
				Authorization: "Bearer " + auth.token,
			});

			// The http-hook wraps the response in a data property
			const foldersArray = response?.data || [];
			setFolders(foldersArray);
		} catch (err) {
			console.error("Error fetching folders:", err);
			setFolders([]); // Set empty array on error
		}
	}, [sendRequest, auth.token]);

	useEffect(() => {
		fetchFolders();
	}, [fetchFolders]);

	const createFolder = useCallback(
		async (name, orgId) => {
			try {
				const response = await sendRequest(
					"/api/folders",
					"POST",
					JSON.stringify({
						name: name.trim(),
						orgId: orgId,
						createdAt: new Date(),
						items: 0,
					}),
					{
						"Content-Type": "application/json",
						Authorization: "Bearer " + auth.token,
					}
				);
				await fetchFolders();
				return response.data;
			} catch (err) {
				console.error("Error creating folder:", err);
				throw err;
			}
		},
		[sendRequest, auth.token, fetchFolders]
	);

	const deleteFolder = useCallback(
		async (folderId) => {
			try {
				await sendRequest(`/api/folders/${folderId}`, "DELETE", null, {
					Authorization: "Bearer " + auth.token,
				});
				await fetchFolders(); // Refresh the folders list
				return true;
			} catch (err) {
				console.error("Error deleting folder:", err);
				throw err;
			}
		},
		[sendRequest, auth.token, fetchFolders]
	);

	const togglePinFolder = useCallback(
		async (folderId) => {
			try {
				const response = await sendRequest(
					`/api/folders/${folderId}/toggle-pin`,
					"POST",
					null,
					{
						Authorization: "Bearer " + auth.token,
					}
				);
				await fetchFolders(); // Refresh the folders list
				return response.data.isPinned;
			} catch (err) {
				console.error("Error toggling folder pin:", err);
				throw err;
			}
		},
		[sendRequest, auth.token, fetchFolders]
	);

	const uploadFile = useCallback(
		async (file, title, folderId, orgId) => {
			try {
				setUploading(true);
				const formData = new FormData();
				formData.append("file", file);
				formData.append("title", title);
				formData.append("orgId", orgId);

				const response = await sendRequest(
					`/api/folders/${folderId}/files`,
					"POST",
					formData,
					{
						Authorization: "Bearer " + auth.token,
					},
					true // This indicates it's a FormData request
				);
				console.log("Upload file response:", response);
				setUploading(false);
				await fetchFolders(); // Refresh the folders list
				return response.data;
			} catch (err) {
				console.error("Error uploading file:", err);
				throw err;
			} finally {
				setUploading(false);
			}
		},
		[sendRequest, auth.token, fetchFolders]
	);

	//Handle file deletion using api/folders/:folderId/files/:fileId/delete DELETE endpoint

	const handleDelete = useCallback(
		async (fileId, folderId) => {
			try {
				await sendRequest(
					`/api/folders/${folderId}/files/${fileId}/delete`,
					"DELETE",
					null,
					{
						Authorization: "Bearer " + auth.token,
					}
				);
				await fetchFolders();
				return true;
			} catch (err) {
				console.error("Error deleting file:", err);
				throw err;
			}
		},
		[sendRequest, auth.token, fetchFolders]
	);



	return {
		folders,
		isLoading,
		error,
		clearError,
		fetchFolders,
		createFolder,
		deleteFolder,
		togglePinFolder,
		uploadFile,
		handleDelete,
		uploading,
	};
};

export default useFolders;
